import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { stubFalse } from 'lodash';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getChildren } from '../../../../../../common/elements/utils/elementTraversalUtils';
import { getCollapsedCommentPopupId } from '../../../../../../common/elements/utils/collapsedCommentPopupUtils';

// Actions
import { createAndEditElement } from '../../../../../element/actions/elementActions';
import { startEditingComment } from '../../../../../element/comment/store/localCommentActions';
import { togglePopup } from '../../../../../components/popupPanel/popupActions';

// Components
import Icon from '../../../../../components/icons/Icon';
import ToolbarTool from '../../ToolbarTool';

// Selectors
import { getCurrentBoardId } from '../../../../../reducers/currentBoardId/currentBoardIdSelector';
import { getElements } from '../../../../../element/selectors/elementsSelector';

// Types
import { ImMNElement, MNElement } from '../../../../../../common/elements/elementModelTypes';
import { ElementType } from '../../../../../../common/elements/elementTypes';

// Constants
import { ELEMENT_CREATION_SOURCES } from '../../../../../../common/elements/elementConstants';
import { BoardSections } from '../../../../../../common/boards/boardConstants';
import { CommentTriggerOrigin } from '../../../../../../common/comments/commentConstants';

type CommentToolProps = {
    selectedElements: Immutable.List<ImMNElement>;
    name: string;
};

const CommentTool: FC<CommentToolProps> = (props) => {
    const { name, selectedElements } = props;
    const currentBoardId = useSelector(getCurrentBoardId);
    const dispatch = useDispatch();

    const element = selectedElements.first();
    const elementId = getElementId(element);

    const elements = useSelector(getElements);

    const addComment = () => {
        const children = getChildren(elements, elementId).valueSeq().toJS();
        const existingThreadId = children.find(
            (el: MNElement) => el.elementType === ElementType.COMMENT_THREAD_TYPE,
        )?.id;

        if (existingThreadId) {
            // Already has an attached comment, so append to the thread
            const popupId = getCollapsedCommentPopupId(existingThreadId);
            dispatch(togglePopup({ popupId, predicateFn: stubFalse }));
            dispatch(
                startEditingComment({
                    _id: existingThreadId,
                    elementId: existingThreadId,
                    triggerOrigin: CommentTriggerOrigin.TOOLBAR,
                }),
            );
            return;
        }

        // otherwise create a new comment thread
        dispatch(
            createAndEditElement({
                elementType: ElementType.COMMENT_THREAD_TYPE,
                creationSource: ELEMENT_CREATION_SOURCES.TOOLBAR,
                currentBoardId,
                location: {
                    parentId: elementId,
                    section: BoardSections.ATTACHED,
                    position: {
                        x: 'calc(100% - 2rem)',
                        y: '2rem',
                    },
                },
            }),
        );
    };

    return (
        <div className="CommentTool">
            <ToolbarTool {...props} name={name || 'Comment'} onClick={addComment}>
                <Icon name={`toolbar-comment`} />
            </ToolbarTool>
        </div>
    );
};

export default CommentTool;
